import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, Vote, Filter, X, ShieldAlert, Building2,
  Calendar, Users, Percent, MapPin, ExternalLink, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO','BR'];
const CARGOS = ['Presidente da República','Governador','Senador','Deputado Federal','Deputado Estadual','Prefeito','Vereador'];

const CenarioEleitoral = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    cargo: '', uf: '', ano: '', turno: '', instituto: '',
  });

  const load = useCallback(async (p = 1, f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(p));
      params.set('limit', '10');
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await axios.get(`${API}/public/pesquisas?${params.toString()}`, { timeout: 20000 });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch (e) {
      toast.error('Não foi possível carregar as pesquisas.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(1, filters); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    setShowFilters(false);
    load(1, filters);
  };
  const clearFilters = () => {
    const empty = { cargo: '', uf: '', ano: '', turno: '', instituto: '' };
    setFilters(empty);
    load(1, empty);
  };

  const filterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="cenario-eleitoral-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#112240] flex items-center justify-center shrink-0">
            <Vote className="w-5 h-5 text-[#64C8F0]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-3xl font-black text-[#0F1C2E] leading-tight">Cenário Eleitoral</h1>
            <p className="text-xs md:text-sm text-[#475569] leading-snug">
              Pesquisas oficialmente registradas, organizadas para facilitar o acesso do cidadão.
            </p>
          </div>
        </div>

        {/* Aviso fixo */}
        <Card className="p-3 border-2 border-[#F59E0B] bg-[#FEF3C7]" data-testid="cenario-aviso">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
            <p className="text-xs text-[#78350F] leading-relaxed">
              O Tô de Olho <strong>não produz</strong> pesquisas eleitorais. Os dados são organizados a partir de registros e documentos oficiais do TSE.
            </p>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(true)}
          className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9]"
          data-testid="btn-open-filters"
        >
          <Filter className="w-3.5 h-3.5 mr-1.5" />
          Filtrar {filterCount > 0 ? `(${filterCount})` : ''}
        </Button>
        {filterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#475569]" data-testid="btn-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" /> Limpar
          </Button>
        )}
        <span className="text-xs text-[#94A3B8] ml-auto" data-testid="pesquisas-total">
          {total} pesquisa(s) encontrada(s)
        </span>
      </div>

      {/* Drawer de filtros (mobile-first, ocupa tela inteira) */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowFilters(false)}>
          <div
            className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="filters-drawer"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-black text-[#0F1C2E]">Filtros</h2>
              <button onClick={() => setShowFilters(false)} className="p-2 rounded hover:bg-[#F1F5F9]" data-testid="btn-close-filters" aria-label="Fechar filtros">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#475569] uppercase tracking-wider">Cargo</label>
                <select
                  className="w-full mt-1 h-11 border border-[#E2E8F0] rounded-md px-3 text-sm"
                  value={filters.cargo}
                  onChange={(e) => setFilters({ ...filters, cargo: e.target.value })}
                  data-testid="filter-cargo"
                >
                  <option value="">Todos</option>
                  {CARGOS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-[#475569] uppercase tracking-wider">Estado (UF)</label>
                <select
                  className="w-full mt-1 h-11 border border-[#E2E8F0] rounded-md px-3 text-sm"
                  value={filters.uf}
                  onChange={(e) => setFilters({ ...filters, uf: e.target.value })}
                  data-testid="filter-uf"
                >
                  <option value="">Todos</option>
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#475569] uppercase tracking-wider">Ano</label>
                  <select
                    className="w-full mt-1 h-11 border border-[#E2E8F0] rounded-md px-3 text-sm"
                    value={filters.ano}
                    onChange={(e) => setFilters({ ...filters, ano: e.target.value })}
                    data-testid="filter-ano"
                  >
                    <option value="">Todos</option>
                    <option value="2026">2026</option>
                    <option value="2024">2024</option>
                    <option value="2022">2022</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-[#475569] uppercase tracking-wider">Turno</label>
                  <select
                    className="w-full mt-1 h-11 border border-[#E2E8F0] rounded-md px-3 text-sm"
                    value={filters.turno}
                    onChange={(e) => setFilters({ ...filters, turno: e.target.value })}
                    data-testid="filter-turno"
                  >
                    <option value="">Todos</option>
                    <option value="1">1º turno</option>
                    <option value="2">2º turno</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#475569] uppercase tracking-wider">Instituto</label>
                <Input
                  value={filters.instituto}
                  onChange={(e) => setFilters({ ...filters, instituto: e.target.value })}
                  placeholder="Ex: Datafolha, Ipec, Quaest"
                  className="mt-1 h-11"
                  data-testid="filter-instituto"
                />
              </div>
              <Button onClick={applyFilters} className="w-full h-11 bg-[#006030] hover:bg-[#005028] text-white mt-4" data-testid="btn-apply-filters">
                Aplicar filtros
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className="py-10 text-center text-sm text-[#475569]" data-testid="cenario-loading">Carregando pesquisas...</div>
      ) : items.length === 0 ? (
        <Card className="p-6 border border-[#E2E8F0] text-center" data-testid="cenario-empty">
          <Info className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
          <p className="text-sm text-[#475569]">
            Nenhuma pesquisa encontrada com esses filtros. O banco será enriquecido diariamente com o sync oficial do TSE.
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="pesquisas-list">
          {items.map((p) => <PesquisaCard key={p.nr_pesquisa_tse} p={p} />)}
        </div>
      )}

      {/* Paginação simples */}
      {items.length > 0 && total > 10 && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => load(page - 1, filters)}
            data-testid="btn-prev-page"
          >
            Anterior
          </Button>
          <span className="text-xs text-[#94A3B8]">Página {page} de {Math.ceil(total / 10)}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(total / 10)}
            onClick={() => load(page + 1, filters)}
            data-testid="btn-next-page"
          >
            Próxima
          </Button>
        </div>
      )}

      <p className="text-[10px] text-[#94A3B8] text-center py-4 border-t border-[#F1F5F9]">
        Fonte oficial. Portal de Dados Abertos do TSE. Atualização diária.
      </p>
    </div>
  );
};

const PesquisaCard = ({ p }) => (
  <Link
    to={`/pesquisa/${p.nr_pesquisa_tse}`}
    className="block"
    data-testid={`pesquisa-card-${p.nr_pesquisa_tse}`}
  >
    <Card className="p-4 border border-[#E2E8F0] hover:border-[#006030] transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Nº registro TSE</p>
          <p className="text-sm font-black text-[#0F1C2E]">#{p.nr_pesquisa_tse}</p>
        </div>
        <span className="text-[10px] text-[#475569] bg-[#F1F5F9] px-2 py-0.5 rounded-full font-semibold shrink-0">
          {p.uf || 'BR'}
        </span>
      </div>
      <p className="text-base font-black text-[#0F1C2E] mb-1 line-clamp-2">{p.instituto}</p>
      <p className="text-xs text-[#475569] mb-2">{p.cargo} · {p.turno ? `${p.turno}º turno` : 'turno único'} · {p.abrangencia}</p>
      <div className="flex items-center gap-3 text-[11px] text-[#475569] flex-wrap">
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Divulgada em {p.data_divulgacao || '-'}</span>
        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {p.tamanho_amostra || '-'}</span>
        {p.margem_erro != null && (
          <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3" /> ± {p.margem_erro}%</span>
        )}
      </div>
    </Card>
  </Link>
);

export default CenarioEleitoral;
